import type { ErrorEvent, StackFrame } from '@sentry/react';

const ALLOWED_TAGS = new Set([
  'appVersion',
  'blockFlavour',
  'bootBlocker',
  'distribution',
  'editorVersion',
  'feature',
  'outcome',
  'prop',
  'reconciliationAnomaly',
  'reconciliationPhase',
  'reconciler',
  'repairReason',
]);

const ALLOWED_MESSAGES = new Set([
  'Application boot stalled',
  'Canvas geometry repaired during hydration',
  'Canvas reconciliation invariant violated',
  'Persistent storage denied by browser',
]);

function safeText(value: unknown, maxLength = 160): string | undefined {
  if (typeof value !== 'string' || value.length === 0) return undefined;
  return value.replace(/[\r\n\t]/g, ' ').slice(0, maxLength);
}

function safeFilename(value: unknown): string | undefined {
  const filename = safeText(value, 512);
  if (!filename) return undefined;
  try {
    const url = new URL(filename);
    if (url.protocol === 'http:' || url.protocol === 'https:') {
      return safeText(url.pathname, 240);
    }
    return safeText(url.pathname.split('/').filter(Boolean).at(-1), 240);
  } catch {
    const segments = filename.replace(/\\/g, '/').split('/').filter(Boolean);
    if (segments.length === 0) return undefined;
    return segments.slice(-2).join('/');
  }
}

function sanitizeFrame(frame: StackFrame): StackFrame {
  return {
    ...(safeFilename(frame.filename)
      ? { filename: safeFilename(frame.filename) }
      : {}),
    ...(safeText(frame.function) ? { function: safeText(frame.function) } : {}),
    ...(typeof frame.lineno === 'number' ? { lineno: frame.lineno } : {}),
    ...(typeof frame.colno === 'number' ? { colno: frame.colno } : {}),
    ...(typeof frame.in_app === 'boolean' ? { in_app: frame.in_app } : {}),
  };
}

function sanitizeTags(
  tags: ErrorEvent['tags']
): ErrorEvent['tags'] | undefined {
  if (!tags) return undefined;
  const sanitized = Object.fromEntries(
    Object.entries(tags)
      .filter(([key, value]) => ALLOWED_TAGS.has(key) && value != null)
      .map(([key, value]) => [key, safeText(String(value))])
      .filter((entry): entry is [string, string] => entry[1] !== undefined)
  );
  return Object.keys(sanitized).length > 0 ? sanitized : undefined;
}

/**
 * Final browser diagnostics boundary. Only release metadata, allowlisted tags,
 * exception types, and source-location frames can leave the client.
 */
export function sanitizeSentryEvent(event: ErrorEvent): ErrorEvent {
  const tags = sanitizeTags(event.tags);
  const values = event.exception?.values?.map(value => ({
    ...(safeText(value.type) ? { type: safeText(value.type) } : {}),
    ...(value.stacktrace?.frames
      ? {
          stacktrace: {
            frames: value.stacktrace.frames.map(sanitizeFrame),
          },
        }
      : {}),
  }));

  return {
    type: undefined,
    ...(event.event_id ? { event_id: event.event_id } : {}),
    ...(event.timestamp ? { timestamp: event.timestamp } : {}),
    ...(event.platform ? { platform: event.platform } : {}),
    ...(event.level ? { level: event.level } : {}),
    ...(event.release ? { release: event.release } : {}),
    ...(event.environment ? { environment: event.environment } : {}),
    ...(event.dist ? { dist: event.dist } : {}),
    ...(event.message
      ? {
          message: ALLOWED_MESSAGES.has(event.message)
            ? event.message
            : 'Technical error',
        }
      : {}),
    ...(tags ? { tags } : {}),
    ...(values && values.length > 0 ? { exception: { values } } : {}),
  };
}
